import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useSEO } from "../lib/useSEO";
import { Container, SectionHeading, Eyebrow } from "../components/site/ui";

const emptyForm = {
  fullName: "", company: "", role: "", email: "", industry: "",
  companySize: "", message: "", interest: "", privacyConsent: false, website: "",
};

export default function Contact() {
  const { t, lang } = useLang();
  const c = t.contact;
  useSEO({ title: c.metaTitle, description: c.metaDesc, path: "/contact", lang });

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const industryOptions = t.industries.items.map((i) => i.t);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (form.fullName.trim().length < 2) e.fullName = c.errors.required;
    if (!form.company.trim()) e.company = c.errors.required;
    if (!form.role.trim()) e.role = c.errors.required;
    if (!form.email.trim()) e.email = c.errors.required;
    else if (!emailRe.test(form.email)) e.email = c.errors.email;
    if (!form.industry) e.industry = c.errors.required;
    if (!form.companySize) e.companySize = c.errors.required;
    if (form.message.trim().length < 5) e.message = c.errors.required;
    if (!form.interest) e.interest = c.errors.required;
    if (!form.privacyConsent) e.privacyConsent = c.errors.privacy;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          "form-name": "contact",
          ...form,
          privacyConsent: form.privacyConsent ? "yes" : "no",
        }).toString(),
      });
      if (!response.ok) throw new Error(`Form submission failed: ${response.status}`);
      setDone(true);
    } catch (err) {
      setErrors((e) => ({ ...e, _global: c.errors.generic }));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (field) =>
    `w-full bg-nb-deep border rounded-sm px-3.5 py-2.5 text-sm text-white placeholder-cool-mid transition-colors duration-200 focus:outline-none focus:border-cyan ${
      errors[field] ? "border-destructive" : "border-cool-dark"
    }`;

  const Label = ({ htmlFor, children, required }) => (
    <label htmlFor={htmlFor} className="block text-xs font-mono uppercase tracking-wider text-cool-light mb-1.5">
      {children}{required && <span className="text-cyan"> *</span>}
    </label>
  );

  const Err = ({ field }) => errors[field] ? (
    <p className="mt-1 text-xs text-destructive" data-testid={`error-${field}`}>{errors[field]}</p>
  ) : null;

  if (done) {
    return (
      <section className="pt-32 pb-32" data-testid="contact-success">
        <Container className="max-w-2xl">
          <div className="border border-cyan bg-nb-deep p-10 text-center">
            <CheckCircle2 size={44} className="text-cyan mx-auto" strokeWidth={1.5} />
            <h1 className="mt-5 font-display text-3xl text-white">{c.successTitle}</h1>
            <p className="mt-3 text-cool-light">{c.successBody}</p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <button onClick={() => { setForm(emptyForm); setDone(false); }} data-testid="contact-new-request"
                className="border border-cool-mid text-white text-sm font-semibold px-5 py-3 rounded-sm hover:border-cyan hover:text-cyan transition-colors">
                {c.newRequest}
              </button>
              <Link to="/" className="bg-cyan text-nb-deep text-sm font-semibold px-5 py-3 rounded-sm hover:bg-white transition-colors">Home</Link>
            </div>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <div>
      <section className="pt-28 pb-12 border-b border-cool-dark" data-testid="contact-hero">
        <Container><SectionHeading eyebrow={c.eyebrow} title={c.title} intro={c.intro} /></Container>
      </section>

      <section className="py-16" data-testid="contact-form-section">
        <Container className="max-w-3xl">
          <form name="contact" method="POST" data-netlify="true" data-netlify-honeypot="website"
            onSubmit={submit} noValidate data-testid="contact-form">
            <input type="hidden" name="form-name" value="contact" />
            {/* honeypot */}
            <input type="text" name="website" value={form.website} tabIndex="-1" autoComplete="off"
              onChange={(e) => set("website", e.target.value)}
              style={{ position: "absolute", left: "-9999px", opacity: 0 }} aria-hidden="true" />

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="fullName" required>{c.fields.fullName}</Label>
                <input id="fullName" name="fullName" data-testid="input-fullName" className={inputCls("fullName")}
                  value={form.fullName} onChange={(e) => set("fullName", e.target.value)} />
                <Err field="fullName" />
              </div>
              <div>
                <Label htmlFor="company" required>{c.fields.company}</Label>
                <input id="company" name="company" data-testid="input-company" className={inputCls("company")}
                  value={form.company} onChange={(e) => set("company", e.target.value)} />
                <Err field="company" />
              </div>
              <div>
                <Label htmlFor="role" required>{c.fields.role}</Label>
                <input id="role" name="role" data-testid="input-role" className={inputCls("role")}
                  value={form.role} onChange={(e) => set("role", e.target.value)} />
                <Err field="role" />
              </div>
              <div>
                <Label htmlFor="email" required>{c.fields.email}</Label>
                <input id="email" name="email" type="email" data-testid="input-email" className={inputCls("email")}
                  value={form.email} onChange={(e) => set("email", e.target.value)} />
                <Err field="email" />
              </div>
              <div>
                <Label htmlFor="industry" required>{c.fields.industry}</Label>
                <select id="industry" name="industry" data-testid="input-industry" className={inputCls("industry")}
                  value={form.industry} onChange={(e) => set("industry", e.target.value)}>
                  <option value="">{c.placeholders.select}</option>
                  {industryOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                <Err field="industry" />
              </div>
              <div>
                <Label htmlFor="companySize" required>{c.fields.companySize}</Label>
                <select id="companySize" name="companySize" data-testid="input-companySize" className={inputCls("companySize")}
                  value={form.companySize} onChange={(e) => set("companySize", e.target.value)}>
                  <option value="">{c.placeholders.select}</option>
                  {c.sizes.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                <Err field="companySize" />
              </div>
            </div>

            <div className="mt-5">
              <Label htmlFor="interest" required>{c.fields.interest}</Label>
              <select id="interest" name="interest" data-testid="input-interest" className={inputCls("interest")}
                value={form.interest} onChange={(e) => set("interest", e.target.value)}>
                <option value="">{c.placeholders.select}</option>
                {Object.entries(c.interests).map(([k, v]) => <option key={k} value={v}>{v}</option>)}
              </select>
              <Err field="interest" />
            </div>

            <div className="mt-5">
              <Label htmlFor="message" required>{c.fields.message}</Label>
              <textarea id="message" name="message" rows="5" data-testid="input-message" className={inputCls("message")}
                placeholder={c.placeholders.message}
                value={form.message} onChange={(e) => set("message", e.target.value)} />
              <Err field="message" />
            </div>

            <div className="mt-6 flex items-start gap-3">
              <input id="privacy" name="privacyConsent" type="checkbox" value="yes" data-testid="input-privacy"
                className="mt-1 w-4 h-4 accent-cyan cursor-pointer"
                checked={form.privacyConsent} onChange={(e) => set("privacyConsent", e.target.checked)} />
              <label htmlFor="privacy" className="text-sm text-cool-light cursor-pointer">
                {c.fields.privacy} <Link to="/privacy-policy" className="text-cyan underline">{t.privacy.title}</Link>
              </label>
            </div>
            <Err field="privacyConsent" />

            {errors._global && <p className="mt-4 text-sm text-destructive" data-testid="error-global">{errors._global}</p>}

            <button type="submit" disabled={submitting} data-testid="contact-submit"
              className="mt-8 inline-flex items-center gap-2 bg-cyan text-nb-deep text-sm font-semibold px-6 py-3 rounded-sm hover:bg-white transition-colors disabled:opacity-60">
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? c.sending : c.submit}
            </button>
          </form>
        </Container>
      </section>
    </div>
  );
}
